'use strict'

var Topic = require('./topic')
var Device = require('./device')
var BasUtil = require('@basalte/bas-util')

var P = require('./parser_constants')
var CONSTANTS = require('./constants')
var CryptoJS = require('@gidw/crypto-js')
var z = require('zod')

var SensorScheme = z.object({
  uuid: z.string(),
  name: z.string(),
  translationKey: z.string(),
  state: z.string()
})

var SensorGroupScheme = z.object({
  uuid: z.string(),
  name: z.string(),
  sensors: z.array(SensorScheme).optional(),
  translationKey: z.string()

})

var AlertScheme = z.object({
  uuid: z.string(),
  name: z.string(),
  translationKey: z.string(),
  state: z.string()
})

var AlarmAreasScheme = z.object({
  uuid: z.string(),
  name: z.string(),
  translationKey: z.string(),
  alerts: z.array(AlertScheme).optional(),
  sensorGroups: z.array(SensorGroupScheme).optional(),
  sensors: z.array(SensorScheme).optional()
})

var AlarmCapabilitiesScheme = z.object({
  arm: z.string().optional(),
  partialArm: z.string().optional(),
  disarm: z.string().optional()
})

var AlarmScheme = z.object({
  uuid: z.string(),
  name: z.string(),
  translationKey: z.string(),
  capabilities: AlarmCapabilitiesScheme
})

var AlarmSystemCapabilitiesScheme = z.object({
  arm: z.string().optional(),
  partialArm: z.string().optional(),
  disarm: z.string().optional()
})

var AlarmSystemScheme = z.object({
  uuid: z.string(),
  armDelaySeconds: z.number(),
  disarmDelaySeconds: z.number(),
  triggerDelaySeconds: z.number(),
  timeoutDurationSeconds: z.number(),
  capabilities: AlarmSystemCapabilitiesScheme,
  state: z.string(),
  alarms: z.array(AlarmScheme).optional(),
  alarmAreas: z.array(AlarmAreasScheme).optional()
})

var SecurityServerResponseDataScheme = z.object({
  action: z.string(),
  alarmSystems: z.array(AlarmSystemScheme).optional(),
  salt: z.string().optional()
})

var SecurityServerResponseScheme = z.object({
  id: z.number(),
  success: z.boolean().optional(),
  data: SecurityServerResponseDataScheme.optional()
})

/**
 * Class representing security topic
 *
 * @constructor
 * @extends Topic
 * @param {BasCore} basCore
 */
function SecurityTopic (basCore) {

  Topic.call(this)

  this.topic = P.SECURITY

  this._basCore = basCore
}

SecurityTopic.prototype = Object.create(Topic.prototype)
SecurityTopic.prototype.constructor = SecurityTopic

SecurityTopic.EVT_GET_ALARM_SYSTEMS = 'evtGetAlarmSystems'
SecurityTopic.EVT_SYSTEM_STATE_UPDATED = 'evtSystemStateUpdated'
SecurityTopic.EVT_SENSOR_STATE_CHANGED = 'evtSensorStateChanged'

/**
 * Parse a security topic message
 *
 * @param {Object} msg
 */
SecurityTopic.prototype.parse = function (msg) {

  var data, evt

  if (
    msg &&
    msg[P.DATA] &&
    BasUtil.isNEString(msg[P.DATA][P.ACTION])
  ) {

    data = msg[P.DATA]

    switch (data[P.ACTION]) {
      case P.SYSTEM_STATE:
        evt = {
          systemUuid: data[P.SYSTEM_UUID],
          state: data[P.STATE],
          pendingTime: data[P.PENDING_TIME]
        }

        this.emit(SecurityTopic.EVT_SYSTEM_STATE_UPDATED, evt)
        break
      case P.SENSOR_STATE:

        this.emit(SecurityTopic.EVT_SENSOR_STATE_CHANGED, data)
        break
    }
  }
}

/**
 * @returns {Promise}
 */
SecurityTopic.prototype.getAlarmSystems = function () {

  var obj = {}
  obj[P.TOPIC] = P.SECURITY
  obj[P.DATA] = {}
  obj[P.DATA][P.ACTION] = P.GET_ALARM_SYSTEMS

  return this._basCore.requestV2(obj)
    .then(Device.handleResponse)
    .then(this._onSecurityTopicAlarmSystems.bind(this))
}

/**
 * @param {string} systemUuid
 * @param {string} alarmUuid
 * @param {string} code
 * @param {string} [salt]
 * @returns {Promise}
 */
SecurityTopic.prototype.arm = function (systemUuid, alarmUuid, code, salt) {
  var obj = {}
  obj[P.TOPIC] = P.SECURITY
  obj[P.DATA] = {}
  obj[P.DATA][P.ACTION] = P.ARM
  obj[P.DATA][P.SYSTEM_UUID] = systemUuid

  if (salt) {
    obj[P.DATA][P.ALARM_UUID] = alarmUuid
    obj[P.DATA][P.CODE_HASH] = _generateHash(salt, code)
    return this._basCore.requestV2(obj).then(Device.handleResponse)
  } else {
    return this._basCore.requestV2(obj)
      .then(function (response) {
        var saltResponse = response?.[P.DATA]?.[P.SALT]

        if (saltResponse) {
          return this.arm(systemUuid, alarmUuid, code, saltResponse)
        }

        return Promise.reject(CONSTANTS.ERR_INVALID_RESPONSE)
      }.bind(this))
  }
}

/**
 * @param {string} systemUuid
 * @param {string} code
 * @param {string} [salt]
 * @returns {Promise}
 */
SecurityTopic.prototype.disarm = function (systemUuid, code, salt) {
  var obj = {}
  obj[P.TOPIC] = P.SECURITY
  obj[P.DATA] = {}
  obj[P.DATA][P.ACTION] = P.DISARM
  obj[P.DATA][P.SYSTEM_UUID] = systemUuid

  if (salt) {
    obj[P.DATA][P.CODE_HASH] = _generateHash(salt, code)
    return this._basCore.requestV2(obj).then(Device.handleResponse)
  } else {
    return this._basCore.requestV2(obj)
      .then(function (response) {
        var saltResponse = response?.[P.DATA]?.[P.SALT]

        if (saltResponse) {
          return this.disarm(systemUuid, code, saltResponse)
        }

        return Promise.reject(CONSTANTS.ERR_INVALID_RESPONSE)
      }.bind(this))
  }
}

/**
 * @private
 * @param message
 * @returns {Promise}
 */
SecurityTopic.prototype._onSecurityTopicAlarmSystems =
    function (message) {

      if (SecurityServerResponseScheme.safeParse(message).success) {

        return message[P.DATA][P.ALARM_SYSTEMS]
      }
      return Promise.reject(CONSTANTS.ERR_INVALID_RESPONSE)
    }

function _generateHash (salt, code) {
  var h = CryptoJS.createHash()
  h.algorithm = 'sha256'
  h.update(salt + code)
  return h.digest()
}

module.exports = SecurityTopic
